import { useStore } from '@nanostores/react';
import type { ReactNode } from 'react';
import { TokenUsageBadge } from './TokenUsageBadge';
import { AccountDisplay } from '~/components/ui/AccountDisplay';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { TableRow } from '~/components/ui/Table';
import { organizationMembersStore, type MembersTokens } from '~/lib/stores/organizationMembers';
import { userStore } from '~/lib/stores/user';
import type { OrganizationMembership } from '~/types/organization';
import type { EffectiveTier } from '~/utils/effectiveTier';
import {
  computeMonthlyRecurringTokensLeft,
  computeReferralTokensAggregate,
  computeTokensLeftForBalance,
} from '~/utils/tokens-stats';

interface Props {
  isLoading: boolean;
  members: OrganizationMembership[];
}

export const TokenUsageMembersTableContent = ({ isLoading, members }: Props) => {
  const tier = useStore(userStore.getTierStore());

  const membersTokens = useStore(organizationMembersStore.membersTokens);
  const isLoadingMembersTokens = useStore(organizationMembersStore.isLoadingMembersTokens);

  if (isLoading) {
    return (
      <InactiveRow>
        <LoadingDots text="Loading members..." />
      </InactiveRow>
    );
  }

  if (members.length === 0) {
    return (
      <InactiveRow>
        <div className="text-center">No members</div>
      </InactiveRow>
    );
  }

  return members.map((member) => (
    <Row
      key={member.id}
      member={member}
      membersTokens={membersTokens}
      isLoadingMembersTokens={isLoadingMembersTokens}
      tier={tier}
    />
  ));
};

interface RowProps {
  member: OrganizationMembership;
  membersTokens: MembersTokens;
  isLoadingMembersTokens: boolean;
  tier?: EffectiveTier;
}

const Row = ({ member, membersTokens, isLoadingMembersTokens, tier }: RowProps) => {
  const tokenStats = member.user?.providerId ? membersTokens[Number(member.user.providerId)] : undefined;

  const referralTokens = computeReferralTokensAggregate(tokenStats, tier);
  const tokensLeft = computeMonthlyRecurringTokensLeft(tokenStats);
  const remainingReferralTokens = computeTokensLeftForBalance(referralTokens);
  const remainingPurchasedTokens = computeTokensLeftForBalance(tokenStats?.overflow);
  const remainingSpecialTokens = computeTokensLeftForBalance(tokenStats?.specialTokens);

  const maxMonthlyTokens = tokenStats?.maxPerMonth ?? 0;

  return (
    <TableRow key={member.id} className="grid grid-cols-2 items-center">
      <AccountDisplay
        user={member.user}
        title={member.user.name || member.user.username}
        description={member.user.email}
        className="min-w-0"
      />

      <div className="text-left text-sm text-bolt-ds-textSecondary">
        {isLoadingMembersTokens ? (
          <LoadingDots text="Loading tokens..." />
        ) : (
          <>
            {tokenStats ? (
              <TokenUsageBadge
                tokens={{
                  total: maxMonthlyTokens,
                  left: tokensLeft,
                }}
                purchasedTokens={remainingPurchasedTokens}
                referralTokens={remainingReferralTokens}
                specialTokens={remainingSpecialTokens}
              />
            ) : (
              'N/A'
            )}
          </>
        )}
      </div>
    </TableRow>
  );
};

interface InactiveRowProps {
  children: ReactNode;
}

const InactiveRow = ({ children }: InactiveRowProps) => {
  return <TableRow className="w-full text-sm text-bolt-ds-textTertiary">{children}</TableRow>;
};
